import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import BrowseListContainer from './BrowseListContainer';
import { SiteSettings } from 'src/scripts/utils/environment';
import PlpPageHeader from './PlpPageHeader';
import { getBrowseApiHeaders, NON_FILTER_QUERY_PARAMS } from '../../search-page/SearchPageUtils/constants';
import '../../search-page/styles.scss';
import '../../../../styles/components/pagination.scss';
import '../../../../styles/templates/search.scss';
import '../../product-listing-page/style.scss';
import { productServiceClient } from '../../_shared/apolloClientUtils';
import { loader } from 'graphql.macro';
import AloSidebar from '../../_shared/ui-components/YogaUI/alo-ui/AloSidebar';

import {
  BrFilter,
  CollectionDataProductCategoryFragment,
  GetCollectionDataQueryVariables,
} from 'src/generated/product-service-graphql';
import { searchRespProductServiceProductToProductNode } from '../../../sections/header/Search/utils';
import { PlpContext } from '../PLPContext';
import BloomreachFilters from './BloomreachFilters';
import { MUI_MOBILE_BREAKPOINT } from '../../../utils/constants';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useDidUpdateEffect, useSearchParamHelpers } from '../../_shared/react-hooks';
import { getFiltersCount } from '../PlpUtils/getFiltersCount';
import ButtonWithProgress from '../../_shared/ui-components/YogaUI/ButtonWithProgress';
import { ServerPlpData } from 'src/@types/window-extended';
import { useParams } from 'react-router-dom';
import { getIdFromShopifyGraphQLId } from 'src/scripts/utils/shopify';
import { SORT_BY_KEY } from './sorting-utils';
import { useBrFilters } from './useBrFilters';
import {
  BloomreachPageTypes,
  fireGA4PLPfilterTracking,
  fireGA4viewPLPproductsEvent,
  pushBloomreachPageViewEventToDataLayer,
} from 'src/scripts/utils/gtm-data-layer';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';
import { noop } from 'lodash-es';
import { IS_NATIVE_APP_CLIENT, recordFailedToFetchErrorToNewRelic } from 'src/scripts/utils/helpers';
import { AppExlusiveOverlay } from '../../_shared/ui-components/AppExclusive';
import MultiCollectionToggle from './MultiCollectionToggle';
import { getColorName, validateSortBy } from '../PlpUtils/plpHelper';
import { pushPageViewEventToFS } from 'src/scripts/utils/full-story';

const ITEMS_PER_PAGE = 15;
const GetCollectionGQL = loader('./GetCollectionData.product-service.graphql');

const PlpPageContainer = () => {
  const [plpData, setPlpData] = useState<CollectionDataProductCategoryFragment | null>(null);
  const [brFilters, setBrFilters] = useState<BrFilter[]>([]);
  const offsetRef = useRef<number>(0);

  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });
  const desktopView = useMediaQuery('(min-width: 991.98px)');
  const { searchParams } = useSearchParamHelpers();
  const { isPlpFiltersDrawerOpen, togglePlpFiltersDrawer } = useContext(PlpContext);

  const { id = '', colorway } = useParams();
  const collectionHandle = colorway ? 'color' : id;
  const isShopByColor = collectionHandle === 'color';
  const colorTagFromURL = colorway ? colorway : isShopByColor ? 'color-black-color' : null;

  const serverPlpData: ServerPlpData | undefined = window.server_plp_data;
  const selectedSortKey = validateSortBy(searchParams.get(SORT_BY_KEY));

  const {
    handlePendingFilterChange,
    handleClearAllFilters,
    closeFiltersSidebar,
    appliedFilters,
    availableFilters,
  } = useBrFilters({
    brFilters,
    isPlp: true,
  });

  useEffect(() => {
    setBrFilters(plpData?.products?.filters || []);
  }, [plpData]);

  const initialQueryVariables: GetCollectionDataQueryVariables = useMemo(() => {
    return {
      handle: collectionHandle,
      ...(isShopByColor
        ? {
            tagHandle: colorTagFromURL,
          }
        : {}),
      offset: 0,
      limit: ITEMS_PER_PAGE,
      sortKey: selectedSortKey,
      filters: appliedFilters,
    };
  }, [collectionHandle, isShopByColor, colorTagFromURL, selectedSortKey, appliedFilters]);

  useEffect(() => {
    offsetRef.current = 0;
  }, [initialQueryVariables]);

  const { data, loading, fetchMore, networkStatus } = useQuery(GetCollectionGQL, {
    client: productServiceClient,
    context: { headers: getBrowseApiHeaders() },
    variables: initialQueryVariables,
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      recordFailedToFetchErrorToNewRelic('PlpPageContainer', e, {
        collectionHandle,
        colorTagFromURL,
        sortKey: selectedSortKey,
      });
      console.error(e);
    },
  });

  useEffect(() => {
    const collectionResult = data?.productsByCollectionHandle;
    if (collectionResult?.__typename === 'ProductCategory') {
      setPlpData(collectionResult);
    }
  }, [data]);

  const collectionShopifyId = plpData?.id ? getIdFromShopifyGraphQLId(plpData.id) : '';
  const merchant = plpData?.products?.meta?.defaultSortOrderPoweredBy || '';
  const totalCount = plpData?.products?.totalCount || 0;
  const handleLoadMore = () => {
    const newOffset = offsetRef.current + ITEMS_PER_PAGE;
    if (plpData && newOffset >= totalCount) return;
    const fetchMoreVariables = {
      ...initialQueryVariables,
      offset: newOffset,
    };

    fetchMore({ variables: fetchMoreVariables });
    offsetRef.current = newOffset;
  };

  useEffect(() => {
    if (serverPlpData) {
      // Bloomreach page view pixel
      const bloomreachPlpData = {
        cat_id: serverPlpData.handle,
        cat: serverPlpData.title,
      };
      pushBloomreachPageViewEventToDataLayer(BloomreachPageTypes.PLP, bloomreachPlpData);
      //[REVIST] For FS POC
      pushPageViewEventToFS(bloomreachPlpData);
    }
  }, [serverPlpData]);

  useEffect(() => {
    const appliedFiltersArray = Array.from(searchParams.entries()).filter(
      ([key]) => !NON_FILTER_QUERY_PARAMS.includes(key)
    );
    if (appliedFiltersArray.length) fireGA4PLPfilterTracking(appliedFiltersArray);
  }, [searchParams]);

  useEffect(() => {
    if (!plpData) return;
    const startIndex = offsetRef.current;
    const productNodes = plpData?.products?.nodes || [];
    const newlyFetchedProducts = productNodes
      .slice(startIndex, productNodes.length)
      .map((p) => searchRespProductServiceProductToProductNode(p));
    fireGA4viewPLPproductsEvent(newlyFetchedProducts, collectionHandle, startIndex, collectionShopifyId, merchant);
  }, [plpData, collectionShopifyId, collectionHandle, merchant]);

  const stickyUniBannerIsEnabled = SiteSettings?.uniBannerStickyEnable;

  const totalAppliedFilterCount = getFiltersCount(appliedFilters);

  useDidUpdateEffect(
    () => {
      try {
        const targetNode: HTMLElement | null = document.querySelector('#PLPContainer .right');
        const navNode: HTMLElement | null = document.querySelector('nav');
        // the disappearing nav makes this slightly annoying
        const adjustment = navNode ? navNode.clientHeight : 83;
        if (targetNode) {
          const yDelta = targetNode.getBoundingClientRect().top - adjustment;
          if (yDelta < 0) window.scrollBy(0, yDelta);
        }
      } catch (err) {}
    },
    // ? [REVISIT] technically it could be a little unsafe to trigger scrollToTop on every queryparam change
    [searchParams]
  );

  const FiltersUI =
    brFilters.length > 0 ? (
      <div className="FiltersUI">
        {desktopView ? <MultiCollectionToggle /> : null}
        <div className="heading">
          <h6 className="h6">
            <span>FILTERS</span> <span>{totalAppliedFilterCount ? `(${totalAppliedFilterCount})` : ''}</span>
          </h6>
          {!!totalAppliedFilterCount && (
            <button
              type="button"
              className="clear-all-filters body-text-link-small grey"
              onClick={handleClearAllFilters}
            >
              Clear All
            </button>
          )}
        </div>
        <BloomreachFilters
          filters={brFilters.map((brFilters) => {
            if (brFilters.__typename === 'SizeFilter') {
              return {
                ...brFilters,
                __typename: 'GroupedListFilter',
              };
            }
            return brFilters;
          })}
          availableFilters={availableFilters}
          selectedFilters={appliedFilters}
          onChange={handlePendingFilterChange}
          showSortOptions={false}
          sortOptions={plpData?.products?.sorts || []}
          showSelectedFilterLabels={true}
          disableShowMore={true}
        />
      </div>
    ) : null;

  const collectionTitle =
    isShopByColor && colorTagFromURL ? `All ${getColorName(colorTagFromURL)}` : serverPlpData?.title || '';
  const nextOffset = offsetRef.current + ITEMS_PER_PAGE;

  return (
    <>
      {/* Note: order matters between (PLPContainer & PlpAppExclusiveOverlay), makes use of the `+` selector  */}
      <ErrorBoundary onError={noop}>
        <PlpAppExclusiveOverlay />
      </ErrorBoundary>
      <div id="PLPContainer" className="search-page" mobile-view={isMobile}>
        <>
          <div className="container-fluid fw-grid__wrapper content-wrapper">
            <PlpPageHeader
              title={collectionTitle}
              resultsQuantity={totalCount}
              totalAppliedFilterCount={totalAppliedFilterCount}
              filtersCount={brFilters.length}
              openPlpFiltersDrawer={() => togglePlpFiltersDrawer(true)}
              sortOptions={plpData?.products?.sorts || []}
              isLoading={loading && networkStatus !== NetworkStatus.ready}
              clearAllFilters={handleClearAllFilters}
            />
            <div className="main-content">
              {brFilters.length > 0 ? (
                desktopView ? (
                  <div className="left">
                    <div
                      className={`sticky-left-side-filters ${
                        stickyUniBannerIsEnabled ? 'extra-top-spacing' : 'normal-top-spacing'
                      }`}
                    >
                      {FiltersUI}
                    </div>
                  </div>
                ) : (
                  <AloSidebar
                    mobileWidth={`300px`}
                    wrapperClass="filter-drawer-container plp"
                    visible={isPlpFiltersDrawerOpen}
                    onClose={closeFiltersSidebar}
                    title={``}
                    headerBorder={true}
                    anchor={'left'}
                    desktopWidth={'300px'}
                  >
                    <div className="filters-wrapper">
                      {FiltersUI}
                      <div className="sticky-filters-button">
                        <ButtonWithProgress
                          options={{
                            onClick: closeFiltersSidebar,
                            showProgressAnimation: loading,
                            buttonTypeClass: 'button-primary',
                            cta: `Apply`,
                          }}
                        />
                      </div>
                    </div>
                  </AloSidebar>
                )
              ) : null}
              <div className="right">
                <BrowseListContainer
                  products={plpData?.products?.nodes?.map((p) => searchRespProductServiceProductToProductNode(p)) || []}
                  onScrollToEnd={handleLoadMore}
                  isLoading={loading && networkStatus !== NetworkStatus.ready}
                  totalProducts={totalCount}
                  clearAllFilters={handleClearAllFilters}
                  collectionHandle={collectionHandle}
                  collectionShopifyId={collectionShopifyId}
                  source="PLP"
                  isPlp={true}
                  merchant={merchant}
                  hasNextPage={nextOffset < totalCount}
                />
              </div>
            </div>
          </div>
        </>
      </div>
    </>
  );
};

export default PlpPageContainer;

const PlpAppExclusiveOverlay = () => {
  const serverPlpData: ServerPlpData | undefined = window.server_plp_data;
  const metafieldValue = serverPlpData?.app_exclusive.copy;
  const hasMetaField = metafieldValue;
  if (!hasMetaField || IS_NATIVE_APP_CLIENT) return null;
  return <AppExlusiveOverlay copy={metafieldValue} />;
};
